import { BraceVarTag, filterClasses, sequenceVarTags, snowyVarCatalog } from "@swivl-mono/helpers";
import { BOT_SEQUENCE_BODY_LOCALES, BOT_SEQUENCE_DEFAULT_BODY_LOCALE, LanguageCode } from "@swivl-mono/types";
import { VariableTypeahead } from "@swivl-mono/ui";
import { useMemo, useState } from "react";

const LOCALE_LABELS: Partial<Record<string, string>> = { [LanguageCode.EN]: "English", [LanguageCode.ES]: "Español" };

interface LocaleBodiesEditorProps {
  bodies: Record<string, string>;
  onChange: (bodies: Record<string, string>) => void;
  disabled: boolean;
  placeholder: string;
  maxLength: number;
  helperText: string;
}

/** The variables a sequence text may use: the Snowy catalog plus the fire-time `Sequence:*` keys. */
export function useSequenceVariables(): BraceVarTag[] {
  return useMemo(() => [...snowyVarCatalog(), ...sequenceVarTags()], []);
}

/**
 * Exact-wording bodies per language: English is always present and required (the fallback for
 * every other language); other allowed languages are added and removed as tabs.
 */
export function LocaleBodiesEditor({ bodies, onChange, disabled, placeholder, maxLength, helperText }: LocaleBodiesEditorProps): React.ReactElement {
  const present = [BOT_SEQUENCE_DEFAULT_BODY_LOCALE, ...Object.keys(bodies).filter((locale) => locale !== BOT_SEQUENCE_DEFAULT_BODY_LOCALE)];
  const [active, setActive] = useState<string>(BOT_SEQUENCE_DEFAULT_BODY_LOCALE);
  const variables = useSequenceVariables();
  const addable = BOT_SEQUENCE_BODY_LOCALES.filter((locale) => !present.includes(locale));
  const activeLocale = present.includes(active) ? active : BOT_SEQUENCE_DEFAULT_BODY_LOCALE;

  const handleBodyChange = (value: string): void => {
    onChange({ ...bodies, [activeLocale]: value });
  };

  const handleAddLocale = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    const locale = event.target.value;
    if (!locale) return;
    onChange({ ...bodies, [locale]: "" });
    setActive(locale);
  };

  const handleRemoveLocale = (locale: string): void => {
    const next = { ...bodies };
    delete next[locale];
    onChange(next);
    setActive(BOT_SEQUENCE_DEFAULT_BODY_LOCALE);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-1">
        {present.map((locale) => (
          <button
            key={locale}
            type="button"
            onClick={() => setActive(locale)}
            className={filterClasses(
              "body-xs rounded-xs px-2.5 py-1 font-semibold",
              locale === activeLocale ? "bg-white text-orange-500 shadow-sm" : "text-grey-700 hover:text-grey-900"
            )}
          >
            {LOCALE_LABELS[locale] ?? locale}
          </button>
        ))}
        {!disabled && addable.length > 0 && (
          <select className="body-xs ml-1 rounded-xs border border-grey-300 bg-white px-1.5 py-1 text-grey-700" value="" onChange={handleAddLocale}>
            <option value="">+ Add language</option>
            {addable.map((locale) => (
              <option key={locale} value={locale}>
                {LOCALE_LABELS[locale] ?? locale}
              </option>
            ))}
          </select>
        )}
        {!disabled && activeLocale !== BOT_SEQUENCE_DEFAULT_BODY_LOCALE && (
          <button type="button" onClick={() => handleRemoveLocale(activeLocale)} className="body-xs ml-auto text-grey-600 hover:text-red-600">
            Remove {LOCALE_LABELS[activeLocale] ?? activeLocale}
          </button>
        )}
      </div>
      <VariableTypeahead
        value={bodies[activeLocale] ?? ""}
        onChange={handleBodyChange}
        variables={variables}
        placeholder={placeholder}
        maxLength={maxLength}
        disabled={disabled}
        helperText={helperText}
      />
    </div>
  );
}
